import React, { useRef } from "react";
import { Row, Col, Form, Button } from "react-bootstrap";
import { useForm, SubmitHandler, SubmitErrorHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import ReCAPTCHA from "react-google-recaptcha";
import * as yup from "yup";

import "../../assets/css/wibb/pages/contact.scss";

import { IRegisterCoach } from "../../types/forms";
import { IAgeLevel } from "../../types/api";
import { PHONE_REGEXP } from "../../lib/globals";
import { useAppDispatch } from "../../hooks/reduxHooks";
import { alertActions } from "../../features/alert/alertSlice";
import { displayFormAlert } from "../../lib/utils";
import { registerUser, verifyEmail } from "../../lib/api";

const RegisterCoach = ({
    ageLevels,
}: {
    ageLevels: IAgeLevel[];
}): React.JSX.Element => {
    const dispatch = useAppDispatch();
    const recaptcha = useRef<ReCAPTCHA>(null);

    const registerSchema = yup.object().shape({
        firstName: yup.string().required("First Name is required"),
        lastName: yup.string().required("Last Name is required"),
        email: yup
            .string()
            .required("Email address is required")
            .email("Email is invalid"),
        confirmEmail: yup
            .string()
            .required("Please confirm your email address")
            .email("Email is invalid")
            .oneOf([yup.ref("email")], "Email does not match"),
        phone: yup
            .string()
            .required("Phone number is required")
            .matches(PHONE_REGEXP, "Phone number is not valid"),
        password: yup
            .string()
            .required("Please choose a password")
            .min(8, "Password length should be at least 8 characters"),
        confirmPassword: yup
            .string()
            .required("Please confirm your password")
            .min(8, "Password length should be at least 8 characters")
            .oneOf([yup.ref("password")], "Passwords do not match"),
        recaptcha: yup.string().nullable(),
        teamName: yup.string().required("Team Name is required"),
        ageLevel: yup
            .mixed<IAgeLevel["ageLevel"]>()
            .required("Age Level is required")
            .oneOf(
                ageLevels.map(a => a.ageLevel) as IAgeLevel["ageLevel"][],
                "Invalid Age Level selected",
            ),
        city: yup.string().required("City is required"),
        state: yup.string().max(2).required("State abbreviation is required"),
    });

    const verifyCaptcha = (): boolean => {
        const recaptchaValue = recaptcha.current?.getValue();
        if (!recaptchaValue) {
            setError("recaptcha", {
                type: "custom",
                message: "Please verify the reCAPTCHA!",
            });
            return false;
        } else {
            clearErrors("recaptcha");
            return true;
        }
    };

    const {
        register,
        handleSubmit,
        setError,
        clearErrors,
        setValue,
        formState: { errors },
    } = useForm({
        mode: "onChange",
        resolver: yupResolver(registerSchema),
    });

    const onSubmit: SubmitHandler<IRegisterCoach> = async data => {
        try {
            await verifyEmail(data.email);
        } catch (e) {
            setError("email", {
                type: "custom",
                message: "The specified email is already registered",
            });
            return false;
        }

        if (verifyCaptcha()) {
            await registerUser(data, "coach")
                .then(() => {
                    dispatch(
                        alertActions.success({
                            title: "Registration Successful",
                            text: "Thank you for registering. We look forward to seeing your team in our tournaments.",
                        }),
                    );
                })
                .catch(e => {
                    dispatch(
                        alertActions.error({
                            title: `The server encountered a problem registering your account.`,
                            text: "If the problem continues, please reach out to an admin.",
                            dismissible: true,
                        }),
                    );
                });
        }
    };

    const onError: SubmitErrorHandler<IRegisterCoach> = (): void => {
        verifyCaptcha();
    };

    const onChangeRecaptcha = async (token: string | null): Promise<void> => {
        const recaptchaValue = recaptcha.current?.getValue();
        setValue("recaptcha", recaptchaValue);
        verifyCaptcha();
        // TODO: Verify response by sending token to back-end API
    };

    return (
        <Form
            onSubmit={handleSubmit(onSubmit, onError)}
            id="registerForm"
            data-testid="registerCoachForm"
        >
            <Row>
                <Col>
                    <h2>Coach Registration</h2>
                </Col>
            </Row>
            <Row>
                <Col>
                    <p>
                        In order to register a team in our league you must be a
                        registered user. To register, please provide the
                        requested info below.
                    </p>
                    <p>
                        We allow 1 user per team, so if the person registering
                        the team is not the head coach, please instruct the head
                        coach to register as a user as well and to contact{" "}
                        <a href="mailto:emma@majorybl.com">Emma</a> to change
                        the registered coach for the team.
                    </p>
                    <p>
                        <strong>
                            Assistant coaches do not need to register as only
                            the head coach has access to the team's Coach's
                            Clubhouse.
                        </strong>
                    </p>
                </Col>
            </Row>
            <Row>
                <Form.Label>Name *</Form.Label>
                <Form.Group as={Col} controlId="firstName">
                    <Form.Control
                        type="text"
                        {...register("firstName")}
                        aria-invalid={errors.firstName ? "true" : "false"}
                    />
                    <Form.Label>First</Form.Label>
                    {errors.firstName &&
                        displayFormAlert({
                            level: "danger",
                            text: errors.firstName.message as string,
                        })}
                </Form.Group>
                <Form.Group as={Col} controlId="lastName">
                    <Form.Control
                        type="text"
                        {...register("lastName")}
                        aria-invalid={errors.lastName ? "true" : "false"}
                    />
                    <Form.Label>Last</Form.Label>
                    {errors.lastName &&
                        displayFormAlert({
                            level: "danger",
                            text: errors.lastName.message as string,
                        })}
                </Form.Group>
            </Row>
            <Row>
                <Col md={6}>
                    <Form.Group controlId="teamName">
                        <Form.Label>Team Name *</Form.Label>
                        <Form.Control
                            type="text"
                            {...register("teamName")}
                            aria-invalid={errors.teamName ? "true" : "false"}
                        />
                        {errors.teamName &&
                            displayFormAlert({
                                level: "danger",
                                text: errors.teamName.message as string,
                            })}
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group controlId="ageLevel">
                        <Form.Label>Age *</Form.Label>
                        <Form.Select
                            defaultValue=""
                            {...register("ageLevel")}
                            aria-invalid={errors.ageLevel ? "true" : "false"}
                        >
                            <option disabled value="">
                                Choose Age
                            </option>
                            {ageLevels.map(a => (
                                <option value={a.ageLevel} key={a.id}>
                                    {a.ageLevel}
                                </option>
                            ))}
                        </Form.Select>
                        {errors.ageLevel &&
                            displayFormAlert({
                                level: "danger",
                                text: errors.ageLevel.message as string,
                            })}
                    </Form.Group>
                </Col>
            </Row>
            <Row>
                <Form.Label>Email *</Form.Label>
                <Form.Group as={Col} controlId="email">
                    <Form.Control
                        type="email"
                        {...register("email")}
                        aria-invalid={errors.email ? "true" : "false"}
                    />
                    <Form.Label>Enter Email</Form.Label>
                    {errors.email &&
                        displayFormAlert({
                            level: "danger",
                            text: errors.email.message as string,
                        })}
                </Form.Group>
                <Form.Group as={Col} controlId="confirmEmail">
                    <Form.Control
                        type="email"
                        {...register("confirmEmail")}
                        aria-invalid={errors.confirmEmail ? "true" : "false"}
                    />
                    <Form.Label>Confirm Email</Form.Label>
                    {errors.confirmEmail &&
                        displayFormAlert({
                            level: "danger",
                            text: errors.confirmEmail.message as string,
                        })}
                </Form.Group>
            </Row>
            <Row>
                <Col md={6}>
                    <Form.Group controlId="phone">
                        <Form.Label>Phone *</Form.Label>
                        <Form.Control
                            type="text"
                            {...register("phone")}
                            aria-invalid={errors.phone ? "true" : "false"}
                        />
                        {errors.phone &&
                            displayFormAlert({
                                level: "danger",
                                text: errors.phone.message as string,
                            })}
                    </Form.Group>
                </Col>
            </Row>
            <Row>
                <Col md={6}>
                    <Form.Group controlId="phone">
                        <Form.Label>City *</Form.Label>
                        <Form.Control
                            type="text"
                            {...register("city")}
                            aria-invalid={errors.city ? "true" : "false"}
                        />
                        {errors.city &&
                            displayFormAlert({
                                level: "danger",
                                text: errors.city.message as string,
                            })}
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group controlId="state">
                        <Form.Label>State *</Form.Label>
                        <Form.Control
                            type="text"
                            {...register("state")}
                            aria-invalid={errors.state ? "true" : "false"}
                        />
                        {errors.state &&
                            displayFormAlert({
                                level: "danger",
                                text: errors.state.message as string,
                            })}
                    </Form.Group>
                </Col>
            </Row>
            <Row>
                <Form.Label>Password *</Form.Label>
                <Form.Group as={Col} controlId="password">
                    <Form.Control
                        type="password"
                        {...register("password")}
                        aria-invalid={errors.password ? "true" : "false"}
                    />
                    <Form.Label>Enter Password</Form.Label>
                    {errors.password &&
                        displayFormAlert({
                            level: "danger",
                            text: errors.password.message as string,
                        })}
                </Form.Group>
                <Form.Group as={Col} controlId="confirmPassword">
                    <Form.Control
                        type="password"
                        {...register("confirmPassword")}
                        aria-invalid={errors.confirmPassword ? "true" : "false"}
                    />
                    <Form.Label>Confirm Password</Form.Label>
                    {errors.confirmPassword &&
                        displayFormAlert({
                            level: "danger",
                            text: errors.confirmPassword.message as string,
                        })}
                </Form.Group>
            </Row>
            <Row>
                <Col xs={3}>
                    <Form.Label>CAPTCHA</Form.Label>
                    <ReCAPTCHA
                        ref={recaptcha}
                        sitekey={process.env.RECAPTCHA_SITE_KEY as string}
                        onChange={onChangeRecaptcha}
                    />
                    {errors.recaptcha &&
                        displayFormAlert({
                            level: "danger",
                            text: errors.recaptcha.message as string,
                        })}
                </Col>
            </Row>
            <Row>
                <Col>
                    <Button variant="primary" type="submit">
                        Submit
                    </Button>
                </Col>
            </Row>
        </Form>
    );
};

export default RegisterCoach;
